import { Button, FormGroup, Input, Label } from '@/components'
import { FlexWrapper } from '@/components/layout/flex/flexWrapper'
import { useAxios } from '@/context/axiosContext'
import { useDialog } from '@/context/dialogContext'
import { PreviewIcon } from '@/pages/feedback/feedbackItem'
import React, { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

interface PostData {
  file?: any
  fileSeq?: string
}

interface FileUploadProps {
  onDeletedFile: (fileSeqs: number[], fileNames: string[]) => void
  onUploadedFile: (
    fileSeqs: number[],
    fileNames: string[],
    fileSources: string[],
  ) => void
  resetUploadTrigger: boolean
  onUploadComplete?: (newFileSeq: number) => void
  fileOrgName?: string
  fileSeq?: number // 기존 파일 시퀀스
  fileSource?: string // 기존 파일 소스
  onFileSelectionChange?: (hasSelectedFiles: boolean) => void
}

const FileUpload: React.FC<FileUploadProps> = ({
  onDeletedFile,
  onUploadedFile,
  resetUploadTrigger,
  onUploadComplete,
  fileOrgName,
  fileSeq,
  fileSource,
  onFileSelectionChange,
}) => {
  const axios = useAxios()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PostData>({
    defaultValues: {},
  })
  const { showAlert, showConfirm } = useDialog()

  // 파일 업로드 관련 상태
  const [uploadFile, setUploadFile] = useState<any | null>(null)
  const [currentUploadedFile, setCurrentUploadedFile] = useState<{
    seq: number
    name: string
    source: string
  } | null>(null)
  const [disabled, setDisabled] = useState(false)

  // 삭제/업로드 배열 관리
  const [deleteQueue, setDeleteQueue] = useState<number[]>([])
  const [uploadQueue, setUploadQueue] = useState<number[]>([])
  const [deleteFileNames, setDeleteFileNames] = useState<string[]>([])
  const [uploadFileNames, setUploadFileNames] = useState<string[]>([])
  const [deleteFileSources, setDeleteFileSources] = useState<string[]>([])
  const [uploadFileSources, setUploadFileSources] = useState<string[]>([])
  const [hasSelectedFile, setHasSelectedFile] = useState(false)

  // 기존 파일이 삭제 대기열에 있는지 확인
  const isExistingFileDeleted = fileSeq && deleteQueue.includes(fileSeq)

  useEffect(() => {
    //console.log('🔍 [FileUpload] onFileSelectionChange 호출:', hasSelectedFile)
    onFileSelectionChange?.(hasSelectedFile)
  }, [hasSelectedFile])

  // 업로드 대기열에 추가하는 함수
  const addToUploadQueue = (
    fileSeq: number,
    fileName: string,
    fileSource: string,
  ) => {
    // console.log('📤 [FileUpload] addToUploadQueue 호출')
    // console.log('  - fileSeq:', fileSeq)
    // console.log('  - fileName:', fileName)
    // console.log('  - fileSource:', fileSource)

    setUploadQueue([fileSeq]) // 한 번에 하나씩만 업로드
    setUploadFileNames([fileName])
    setUploadFileSources([fileSource])

    const seqArray = [fileSeq]
    const nameArray = [fileName]
    const sourceArray = [fileSource]

    // console.log('📤 [FileUpload] 상위 컴포넌트로 업로드 정보 전달')
    // console.log('  - seqArray:', seqArray)
    // console.log('  - nameArray:', nameArray)
    // console.log('  - sourceArray:', sourceArray)

    // 상위 컴포넌트에 업로드 정보 전달
    onUploadedFile(seqArray, nameArray, sourceArray)
  }

  // 삭제 대기열에 추가하는 함수
  const addToDeleteQueue = (
    fileSeq: number,
    fileName?: string,
    fileSource?: string,
  ) => {
    // console.log('🗑️ [FileUpload] addToDeleteQueue 호출')
    // console.log('  - fileSeq:', fileSeq)
    // console.log('  - fileName:', fileName)
    // console.log('  - fileSource:', fileSource)
    // console.log('  - 기존 deleteQueue:', deleteQueue)
    // console.log('  - 기존 deleteFileNames:', deleteFileNames)

    const newQueue = [...deleteQueue, fileSeq]
    const newFileNames = fileName
      ? [...deleteFileNames, fileName]
      : deleteFileNames
    const newFileSources = fileSource
      ? [...deleteFileSources, fileSource]
      : deleteFileSources

    // console.log('🗑️ [FileUpload] 새로운 삭제 정보')
    // console.log('  - newQueue:', newQueue)
    // console.log('  - newFileNames:', newFileNames)
    // console.log('  - newFileSources:', newFileSources)

    // 상태 업데이트
    setDeleteQueue(newQueue)
    if (fileName) {
      setDeleteFileNames(newFileNames)
    }
    if (fileSource) {
      setDeleteFileSources(newFileSources)
    }

    // console.log('📞 [FileUpload] 상위 컴포넌트로 삭제 정보 전달')
    // console.log('  - 전달할 deleteQueue:', newQueue)
    // console.log('  - 전달할 deleteFileNames:', newFileNames)

    // 상위 컴포넌트에 삭제 정보 전달
    onDeletedFile(newQueue, newFileNames)
  }

  // const uploadImage: SubmitHandler<PostData> = async (data) => {
  //   // console.log('🚀 [FileUpload] uploadImage 시작')
  //   // console.log('  - uploadFile:', uploadFile)
  //   // console.log('  - 기존 fileSeq:', fileSeq)
  //   // console.log('  - 기존 fileOrgName:', fileOrgName)
  //   // console.log('  - 기존 fileSource:', fileSource)
  //   // console.log('  - currentUploadedFile:', currentUploadedFile)

  //   if (!uploadFile) {
  //     showAlert({
  //       type: 'error',
  //       title: '업로드할 파일을 선택해 주세요.',
  //     })
  //     return
  //   }

  //   const maxFileSize = 10 * 1024 * 1024

  //   // 파일 크기 체크
  //   if (uploadFile.size > maxFileSize) {
  //     showAlert({
  //       title: '10MB 이상의 파일은 업로드할 수 없어요.',
  //       message: `파일 크기가 ${(uploadFile.size / (1024 * 1024)).toFixed(2)}MB 에요.`,
  //     })
  //     return
  //   }

  //   try {
  //     // 1. 기존 파일이 있으면 삭제 대기열에 추가
  //     if (fileSeq && fileSeq > 0) {
  //       // console.log('🗑️ [FileUpload] 기존 파일을 삭제 대기열에 추가')
  //       // console.log('  - 기존 fileSeq:', fileSeq)
  //       // console.log('  - 기존 fileOrgName:', fileOrgName)
  //       // console.log('  - 기존 fileSource:', fileSource)

  //       addToDeleteQueue(fileSeq, fileOrgName, fileSource || 'EXISTING')
  //     }

  //     // 2. 현재 세션에서 업로드한 파일이 있으면 삭제 대기열에 추가
  //     if (currentUploadedFile) {
  //       // console.log('🗑️ [FileUpload] 현재 업로드된 파일을 삭제 대기열에 추가')
  //       // console.log('  - currentUploadedFile:', currentUploadedFile)

  //       addToDeleteQueue(
  //         currentUploadedFile.seq,
  //         currentUploadedFile.name,
  //         currentUploadedFile.source,
  //       )
  //     }

  //     // 3. 새 파일 업로드
  //     //console.log('📤 [FileUpload] 새 파일 업로드 시작')
  //     const formData = new FormData()
  //     formData.append('file', uploadFile)

  //     const response = await axios.post('/promotion/fileUpload', formData, {
  //       headers: {
  //         'Content-Type': 'multipart/form-data',
  //       },
  //     })

  //     // console.log('📤 [FileUpload] 업로드 응답:', response.data)

  //     if (response.status === 200) {
  //       const { fileSeq: newFileSeq, fileNm } = response.data.data
  //       const newFileSource = 'UPLOAD' // 업로드 파일의 source는 항상 'UPLOAD'

  //       // console.log('✅ [FileUpload] 업로드 성공')
  //       // console.log('  - newFileSeq:', newFileSeq)
  //       // console.log('  - fileNm:', fileNm)
  //       // console.log('  - newFileSource:', newFileSource)

  //       // 4. 성공 후 상태 업데이트
  //       setDisabled(true)
  //       setCurrentUploadedFile({
  //         seq: newFileSeq,
  //         name: fileNm,
  //         source: newFileSource,
  //       })

  //       // 5. 업로드 대기열에 추가
  //       addToUploadQueue(newFileSeq, fileNm, newFileSource)

  //       if (onUploadComplete) {
  //         //console.log('📞 [FileUpload] onUploadComplete 호출:', newFileSeq)
  //         onUploadComplete(newFileSeq)
  //       }

  //       handleUploadComplete()

  //       showAlert({
  //         title: '이미지가 업로드됐어요.',
  //       })
  //     } else {
  //       throw new Error('Upload failed')
  //     }
  //   } catch (error) {
  //     console.error('❌ [FileUpload] 업로드 실패:', error)
  //     showAlert({
  //       type: 'error',
  //       title: '업로드에 실패했어요. 다시 시도해 주세요.',
  //     })
  //   }
  // }

  const uploadImage: SubmitHandler<PostData> = async (data) => {
    console.log('🚀 [FileUpload] uploadImage 시작')
    console.log('  - uploadFile:', uploadFile)

    if (!uploadFile) {
      showAlert({
        type: 'error',
        title: '업로드할 파일을 선택해 주세요.',
      })
      return
    }

    const maxFileSize = 10 * 1024 * 1024

    if (uploadFile.size > maxFileSize) {
      showAlert({
        title: '10MB 이상의 파일은 업로드할 수 없어요.',
        message: `파일 크기가 ${(uploadFile.size / (1024 * 1024)).toFixed(2)}MB 에요.`,
      })
      return
    }

    try {
      // 1. 기존 파일이 있으면 삭제 대기열에 추가
      if (fileSeq && fileSeq > 0) {
        addToDeleteQueue(fileSeq, fileOrgName, fileSource || 'EXISTING')
      }

      // 2. 현재 세션에서 업로드한 파일이 있으면 삭제 대기열에 추가
      if (currentUploadedFile) {
        addToDeleteQueue(
          currentUploadedFile.seq,
          currentUploadedFile.name,
          currentUploadedFile.source,
        )
      }

      // 3. 새 파일 업로드
      console.log('📤 [FileUpload] 새 파일 업로드 시작')
      const formData = new FormData()
      formData.append('file', uploadFile)

      const response = await axios.post('/promotion/fileUpload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      //console.log('📤 [FileUpload] 업로드 응답:', response.data)

      if (response.status === 200) {
        const responseData = response.data.data

        // 응답 데이터 검증
        if (!responseData) {
          throw new Error('서버 응답에 data가 없습니다.')
        }

        const { fileSeq: newFileSeq, fileNm } = responseData

        // fileNm 검증
        if (!fileNm) {
          //console.error('❌ fileNm이 null 또는 undefined:', fileNm)
          throw new Error('서버에서 파일명을 반환하지 않았습니다.')
        }

        const newFileSource = 'UPLOAD'

        // console.log('✅ [FileUpload] 업로드 성공')
        // console.log('  - newFileSeq:', newFileSeq)
        // console.log('  - fileNm:', fileNm)
        // console.log('  - newFileSource:', newFileSource)

        // 4. 성공 후 상태 업데이트
        setDisabled(true)
        setCurrentUploadedFile({
          seq: newFileSeq,
          name: fileNm,
          source: newFileSource,
        })

        // 5. 업로드 대기열에 추가
        addToUploadQueue(newFileSeq, fileNm, newFileSource)

        if (onUploadComplete) {
          onUploadComplete(newFileSeq)
        }

        handleUploadComplete()

        showAlert({
          title: '이미지가 업로드됐어요.',
        })
      } else {
        throw new Error(`Upload failed with status: ${response.status}`)
      }
    } catch (error) {
      //console.error('❌ [FileUpload] 업로드 실패:', error)

      // 더 구체적인 에러 메시지 제공
      const errorMessage = '업로드에 실패했어요. 다시 시도해 주세요.'

      showAlert({
        type: 'error',
        title: errorMessage,
      })
    }
  }

  // FileUpload 컴포넌트의 handleFileAction 수정
  const handleFileAction = () => {
    // console.log('🎯 [FileUpload] handleFileAction 호출')
    // console.log('  - disabled:', disabled)
    // console.log('  - currentUploadedFile:', currentUploadedFile)

    if (disabled) {
      // console.log('🗑️ [FileUpload] 파일 삭제 모드')

      // 삭제 동작
      if (currentUploadedFile) {
        // console.log('🗑️ [FileUpload] 현재 업로드된 파일 삭제')
        // console.log('  - 삭제할 파일:', currentUploadedFile)

        // 새로운 삭제 정보 계산
        const newDeleteQueue = [...deleteQueue, currentUploadedFile.seq]
        const newDeleteFileNames = [
          ...deleteFileNames,
          currentUploadedFile.name,
        ]

        // console.log('🗑️ [FileUpload] 삭제 후 상태')
        // console.log('  - newDeleteQueue:', newDeleteQueue)
        // console.log('  - newDeleteFileNames:', newDeleteFileNames)

        // 상태 업데이트
        setDeleteQueue(newDeleteQueue)
        setDeleteFileNames(newDeleteFileNames)
        if (currentUploadedFile.source) {
          setDeleteFileSources((prev) => [...prev, currentUploadedFile.source])
        }

        // 업로드 관련 상태에서 완전히 제거
        setUploadQueue([]) // 현재 세션에서는 하나씩만 업로드하므로 전체 초기화
        setUploadFileNames([])
        setUploadFileSources([])

        //console.log('📞 [FileUpload] 상위 컴포넌트로 삭제 정보 전달')
        // 상위 컴포넌트에 삭제 정보 전달 (전체 삭제 목록)
        onDeletedFile(newDeleteQueue, newDeleteFileNames)
      }

      // UI 상태 초기화
      setUploadFile(null)
      setCurrentUploadedFile(null)
      setDisabled(false)

      // 추가: 파일 삭제 시 선택된 파일 상태도 false로 설정
      setHasSelectedFile(false)

      const fileInput = document.getElementById('file') as HTMLInputElement
      if (fileInput) {
        fileInput.value = ''
      }
    } else {
      // 업로드 동작
      handleSubmit(uploadImage)()
    }
  }

  // FileUpload 컴포넌트에서 업로드 완료 시 호출해야 할 로직
  const handleUploadComplete = () => {
    // 업로드가 완료되면 선택된 파일 상태를 false로 변경
    onFileSelectionChange?.(false)
    // 기존 onUploadedFile 호출...
  }

  // 초기화 트리거 처리
  useEffect(() => {
    if (resetUploadTrigger) {
      //console.log('🔄 [FileUpload] resetUploadTrigger 실행 - 모든 상태 초기화')

      setUploadFile(null)
      setCurrentUploadedFile(null)
      setDisabled(false)
      setDeleteQueue([])
      setUploadQueue([])
      setDeleteFileNames([])
      setUploadFileNames([])
      setDeleteFileSources([])
      setUploadFileSources([])
      setHasSelectedFile(false)

      const fileInput = document.getElementById('file') as HTMLInputElement
      if (fileInput) {
        fileInput.value = ''
      }

      // console.log('✅ [FileUpload] 초기화 완료')
    }
  }, [resetUploadTrigger])

  // 디버깅용 상태 로그
  useEffect(() => {
    // console.log('📊 [FileUpload] 상태 변경 감지')
    // console.log('  - deleteQueue:', deleteQueue)
    // console.log('  - uploadQueue:', uploadQueue)
    // console.log('  - deleteFileNames:', deleteFileNames)
    // console.log('  - uploadFileNames:', uploadFileNames)
    // console.log('  - deleteFileSources:', deleteFileSources)
    // console.log('  - uploadFileSources:', uploadFileSources)
    // console.log('  - currentUploadedFile:', currentUploadedFile)
    // console.log('  - hasSelectedFile:', hasSelectedFile)
    // console.log('  - disabled:', disabled)
  }, [
    deleteQueue,
    uploadQueue,
    deleteFileNames,
    uploadFileNames,
    deleteFileSources,
    uploadFileSources,
    currentUploadedFile,
    hasSelectedFile,
    disabled,
  ])

  return (
    <form action="#" onSubmit={handleSubmit(uploadImage)} noValidate>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}
      >
        {/* 기존 파일 정보 표시 */}
        {/* {fileOrgName && fileSeq && !currentUploadedFile && (
          <div style={{ fontSize: '14px', color: '#666' }}>
            <span>현재 파일: </span>
            <span
              style={{
                textDecoration: isExistingFileDeleted ? 'line-through' : 'none',
                color: isExistingFileDeleted ? '#ff4444' : '#333',
              }}
            >
              {fileOrgName}
            </span>
            {isExistingFileDeleted && (
              <span style={{ color: '#ff4444', marginLeft: '5px' }}>
                (삭제 예정)
              </span>
            )}
          </div>
        )} */}

        {/* 새로 업로드된 파일 정보 표시 */}
        {/* {currentUploadedFile && (
          <div style={{ fontSize: '14px', color: '#666' }}>
            <span>업로드된 파일: </span>
            <span style={{ color: '#007bff' }}>{currentUploadedFile.name}</span>
          </div>
        )} */}

        <div
          style={{
            display: 'flex',
            justifyItems: 'center',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{ marginRight: '5px' }}>
            <Input
              {...register('file', {})}
              id="file"
              name="file"
              type="file"
              disabled={disabled || resetUploadTrigger}
              onChange={(e: any) => {
                const selectedFiles = e.target.files
                const hasFiles = selectedFiles && selectedFiles.length > 0

                // console.log('📁 [FileUpload] 파일 선택 변경')
                // console.log('  - selectedFiles:', selectedFiles)
                // console.log('  - hasFiles:', hasFiles)

                if (hasFiles) {
                  setUploadFile(selectedFiles[0])
                  //console.log('  - 선택된 파일:', selectedFiles[0])
                } else {
                  setUploadFile(null)
                }

                setHasSelectedFile(hasFiles) // 이것만 해도 useEffect에서 상위 알림 수행됨
              }}
            />
          </div>
          <Button color="neverbe" onClick={handleFileAction}>
            {disabled ? '삭제' : '업로드'}
          </Button>
        </div>

        {/* 개발환경에서 디버깅 정보 표시 */}
        {/* {process.env.NODE_ENV === 'development' && (
          <div
            style={{
              marginTop: '10px',
              fontSize: '12px',
              color: '#666',
              backgroundColor: '#f5f5f5',
              padding: '10px',
              borderRadius: '4px',
            }}
          >
            <div>삭제 대기열: [{deleteQueue.join(', ')}]</div>
            <div>업로드 대기열: [{uploadQueue.join(', ')}]</div>
            <div>삭제 파일명: [{deleteFileNames.join(', ')}]</div>
            <div>업로드 파일명: [{uploadFileNames.join(', ')}]</div>
            <div>삭제 파일 소스: [{deleteFileSources.join(', ')}]</div>
            <div>업로드 파일 소스: [{uploadFileSources.join(', ')}]</div>
            <div>
              현재 업로드 파일:{' '}
              {currentUploadedFile
                ? JSON.stringify(currentUploadedFile)
                : 'null'}
            </div>
            <div>기존 파일 삭제됨: {isExistingFileDeleted ? 'Yes' : 'No'}</div>
          </div>
        )} */}
      </div>
    </form>
  )
}

export default FileUpload
